import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Home,
  MessageSquare,
  Plug,
  LogOut,
  Lock,
  ListChecks,
  Info,
  Clock,
  Bot,
  History,
  House,
  User,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Smartphone,
} from 'lucide-react';
import { logoutUser } from '../../../../../services/session';
import { useAuthSession } from '../../../../../contexts/AuthSessionContext';
import { useBranding } from '../../../../../branding/BrandingContext';
import { ATENDABOT_LOGO, ATENDABOT_PRODUCT_IMAGE, ONE_MENU_IMAGE } from '../../../../../branding/assets';

const Sidebar = ({
  restauranteSlug,
  open = true,
  onToggle,
  mobileOpen = false,
  onNavigate,
  hidePrompt = false,
  promptDraftPending = false,
  promptNotReadyToPublish = false,
  orderControlEnabled = false,
  whatsappDisconnected = false,
}: {
  restauranteSlug?: string | null;
  open?: boolean;
  onToggle?: () => void;
  mobileOpen?: boolean;
  onNavigate?: () => void;
  hidePrompt?: boolean;
  promptDraftPending?: boolean;
  promptNotReadyToPublish?: boolean;
  orderControlEnabled?: boolean;
  whatsappDisconnected?: boolean;
}) => {
  const mini = !open;
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    text: string;
    top: number;
    left: number;
  }>({
    visible: false,
    text: '',
    top: 0,
    left: 0,
  });
  const navigate = useNavigate();
  const branding = useBranding();
  const {
    isFoodUser,
    user,
    restaurant,
    hasProductAccess,
    hasPromptOnlyProductAccess,
    atendabotAtendente,
  } = useAuthSession();

  const isAtendabotBrand = branding?.key === 'atendabot';
  const isFood =
    typeof branding?.isFood === 'boolean' ? branding.isFood : isFoodUser === true;
  const logoSource = isAtendabotBrand
    ? branding?.logos?.primary || ATENDABOT_LOGO
    : isFood
      ? ATENDABOT_PRODUCT_IMAGE
      : ATENDABOT_LOGO;
  const clientName =
    restaurant?.nome
    || user?.name
    || user?.username
    || 'Cliente';
  const promptOnlyAtendabot =
    hasPromptOnlyProductAccess('atendabot')
    && hasProductAccess('atendabot', { allowTrialRestricted: true });

  const navItems = [
    {
      path: '/atendabot',
      name: 'Dashboard',
      icon: <Home size={22} />,
      exact: true,
    },
    {
      path: '/atendabot/conversations',
      name: 'Conversas',
      icon: <MessageSquare size={22} />,
    },
    {
      path: '/atendabot/agenda',
      name: 'Agenda',
      icon: <CalendarDays size={22} />,
      nonFoodOnly: true,
    },
    {
      path: '/atendabot/orders',
      name: 'Pedidos',
      icon: <ListChecks size={22} />,
      exact: true,
      foodOnly: true,
      orderControlOnly: true,
    },
    {
      path: '/atendabot/orders/history',
      name: 'Histórico de Pedidos',
      icon: <History size={22} />,
      foodOnly: true,
      orderControlOnly: true,
    },
    {
      path: '/atendabot/blocked',
      name: 'Exclusão de Atendimento',
      icon: <Lock size={22} />,
    },
    {
      path: '/atendabot/forms/info-extra',
      name: 'Informações Extras',
      icon: <Info size={22} />,
    },
    {
      path: '/atendabot/atalhos',
      name: 'Atalhos',
      icon: <Clock size={22} />,
    },
    {
      path: '/atendabot/transbordo',
      name: 'Transbordo',
      icon: <MessageSquare size={22} />,
    },
    {
      path: '/atendabot/integrations',
      name: 'Integrações',
      icon: <Plug size={22} />,
      foodOnly: true,
    },
    {
      path: '/atendabot/prompt',
      name: 'Configurações IA',
      icon: <Bot size={22} />,
    },
    {
      path: '/atendabot/whatsapp',
      name: 'Conexão WhatsApp',
      icon: <Smartphone size={22} />,
    },
  ];

  const filteredFoodItems = navItems.filter((item) => {
    if (whatsappDisconnected && item.path !== '/atendabot/whatsapp' && item.path !== '/atendabot/prompt') return false;
    if (hidePrompt && !whatsappDisconnected && item.path === '/atendabot/prompt') return false;
    if (item.orderControlOnly && !orderControlEnabled) return false;
    if (item.foodOnly) return isFood;
    if (item.nonFoodOnly) return !isFood;
    return true;
  });

  const atendenteItems = filteredFoodItems.filter((item) => (
    item.path === '/atendabot'
    || item.path === '/atendabot/conversations'
    || item.path === '/atendabot/agenda'
  ));

  const filteredItems = whatsappDisconnected
    ? filteredFoodItems
    : atendabotAtendente
      ? atendenteItems
      : promptOnlyAtendabot
        ? filteredFoodItems.filter((item) => item.path === '/atendabot/prompt' || item.path === '/atendabot/whatsapp')
        : filteredFoodItems;

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const handleBack = () => {
    navigate(isAtendabotBrand ? '/atendabot' : '/products');
  };

  const toggleLabel = open ? 'Recolher menu' : 'Expandir menu';

  const buildOneMenuLink = () => {
    const slug = String(restauranteSlug || '').trim();
    const origin =
      typeof window !== 'undefined' ? String(window.location.origin).replace(/\/+$/, '') : '';
    const base = origin ? `${origin}/cardapio` : '/cardapio';
    if (!slug) return base;
    return `${base}/${encodeURIComponent(slug)}`;
  };
  const oneMenuLink = buildOneMenuLink();

  const handleTooltipShow = (label: string, target: HTMLElement | null, force = false) => {
    if ((!mini && !force) || !target) return;
    const rect = target.getBoundingClientRect();
    setTooltip({
      visible: true,
      text: label,
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  };

  const handleTooltipHide = () => {
    if (!tooltip.visible) return;
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  }, [open]);

  return (
    <aside
      className={`
        ${mobileOpen ? 'fixed inset-y-0 left-0 flex z-40' : 'hidden md:flex'}
        flex-col h-screen bg-[#322F84] border-r border-gray-200
        transition-all duration-300 ease-in-out
        ${mini ? 'w-[4.5rem]' : 'w-64'}
        overflow-x-hidden
      `}
      style={{
        minWidth: mini ? '4.5rem' : '16rem',
        maxWidth: mini ? '4.5rem' : '16rem',
        width: mini ? '4.5rem' : '16rem',
        overflowX: 'hidden',
      }}
    >
      <div className={`flex flex-col w-full ${mini ? 'pt-3 pb-2' : 'pt-3 pb-2'}`}>
        <div
          className={`relative flex w-full items-center ${
            mini ? 'flex-col gap-2 px-1' : 'justify-between px-3'
          }`}
          style={!mini ? { minHeight: '5.5rem' } : undefined}
        >
          {!mini && (
            <img
              src={logoSource}
              alt="AtendaBot Logo"
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 w-auto -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
              style={{ height: isFood ? '4.5rem' : '5.5rem' }}
            />
          )}

          {!mini && !isAtendabotBrand && (
            <button
              type="button"
              onClick={handleBack}
              aria-label="Voltar para produtos"
              title="Voltar para produtos"
              className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20"
            >
              <House size={20} />
            </button>
          )}

          {mini && onToggle && (
            <button
              type="button"
              onClick={onToggle}
              aria-label={toggleLabel}
              onMouseEnter={(event) => handleTooltipShow(toggleLabel, event.currentTarget)}
              onMouseLeave={handleTooltipHide}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20"
            >
              <ChevronRight size={18} />
            </button>
          )}

          {!mini && onToggle && (
            <button
              type="button"
              onClick={onToggle}
              aria-label={toggleLabel}
              onMouseEnter={(event) => handleTooltipShow(toggleLabel, event.currentTarget, true)}
              onMouseLeave={handleTooltipHide}
              className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20"
            >
              <ChevronLeft size={18} />
            </button>
          )}
        </div>

        {mini && (
          <img
            src={logoSource}
            alt="AtendaBot Logo"
            className="w-auto self-center mt-3 transition-all duration-300"
            style={{ height: '3rem' }}
          />
        )}

        {!isAtendabotBrand && !whatsappDisconnected && (
          <div className="mt-3 flex w-full justify-center">
            {mini ? (
            <a
              href={oneMenuLink}
              target="_self"
              rel="noopener noreferrer"
              title="Abrir cardápio no OneMenu"
              className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 p-2 transition hover:border-white/40"
            >
              <img
                src={ONE_MENU_IMAGE}
                alt="OneMenu"
                className="h-8 w-8 rounded-full bg-white p-1 object-contain"
              />
            </a>
          ) : (
            <a
              href={oneMenuLink}
              target="_self"
              rel="noopener noreferrer"
              title="Abrir cardápio no OneMenu"
              className="inline-flex items-center gap-3 rounded-2xl px-4 py-2 text-sm font-semibold text-white shadow-xl shadow-black/20 transition hover:shadow-[0_25px_60px_-8px_rgba(0,0,0,0.65)]"
              style={{
                background:
                  'linear-gradient(135deg, rgba(31,79,191,0.92), rgba(79,110,255,0.8), rgba(242,107,58,0.85))',
              }}
            >
              <img
                src={ONE_MENU_IMAGE}
                alt="OneMenu"
                className="h-8 w-8 rounded-xl bg-white/90 p-1 object-contain"
              />
              <div className="flex flex-col text-left text-xs uppercase tracking-wide">
                <span className="text-[13px] font-bold">OneMenu</span>
                <span className="text-[11px] text-white/80">Gerenciar cardápio</span>
              </div>
            </a>
            )}
          </div>
        )}
      </div>

      <nav
        className={`flex-1 overflow-y-auto ${mini ? 'px-1' : 'px-0'} pt-2 w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:h-0`}
      >
        <ul className="space-y-1 w-full">
          {filteredItems.map((item) => {
            const isDisconnectedPhone = whatsappDisconnected && item.path === '/atendabot/whatsapp';
            const isPromptItem = item.path === '/atendabot/prompt';
            const showNotReady = isPromptItem && promptNotReadyToPublish;
            const showDraftDot = isPromptItem && promptDraftPending;
            const indicatorLabel = showNotReady
              ? 'Atendimento não publicado'
              : showDraftDot
                ? 'Alterações não publicadas'
                : '';
            return (
              <li key={item.path} className="relative group w-full">
                <NavLink
                  to={item.path}
                  end={item.exact}
                  aria-label={isDisconnectedPhone ? `${item.name} — telefone desconectado` : indicatorLabel ? `${item.name} — ${indicatorLabel}` : item.name}
                  onClick={() => onNavigate?.()}
                  onMouseEnter={(event) => handleTooltipShow(
                    isDisconnectedPhone ? `${item.name} — telefone desconectado` : indicatorLabel ? `${item.name} — ${indicatorLabel}` : item.name,
                    event.currentTarget,
                  )}
                  onMouseLeave={handleTooltipHide}
                  className={({ isActive }) =>
                    `flex items-center ${mini ? 'justify-center' : ''} px-2 py-3 text-sm font-medium rounded-lg transition-colors duration-200 w-full ${
                      isDisconnectedPhone
                        ? 'bg-orange-500 text-white font-bold hover:bg-orange-600'
                        : isActive
                        ? 'bg-indigo-50/10 text-white font-semibold'
                        : 'text-indigo-100 hover:bg-white/5 hover:text-white'
                    }`
                  }
                >
                  <span className="relative mr-0">
                    {item.icon}
                    {mini && isDisconnectedPhone && (
                      <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-white ring-2 ring-orange-500" aria-hidden="true" />
                    )}
                    {mini && (showNotReady || showDraftDot) && (
                      <span
                        className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[#322F84] ${
                          showNotReady ? 'bg-rose-500' : 'bg-amber-400'
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                  {!mini && (
                    <span className="ml-3 flex items-center gap-2">
                      {item.name}
                      {isDisconnectedPhone && <AlertCircle size={15} className="flex-shrink-0" aria-hidden="true" />}
                      {showNotReady && (
                        <AlertCircle size={14} className="text-rose-400 flex-shrink-0" aria-hidden="true" />
                      )}
                      {showDraftDot && (
                        <span className="h-2 w-2 flex-shrink-0 rounded-full bg-amber-400" aria-hidden="true" />
                      )}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {tooltip.visible && (
        <div
          className="fixed px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-wide bg-slate-900/95 text-white whitespace-nowrap z-[60] shadow-xl ring-1 ring-white/10 backdrop-blur-sm pointer-events-none"
          style={{ top: tooltip.top, left: tooltip.left, transform: 'translateY(-50%)' }}
        >
          {tooltip.text}
        </div>
      )}

      <div
        className={`mt-auto w-full border-t border-white/10 ${
          mini ? 'flex flex-col items-center px-1 py-3' : 'flex items-center gap-2 px-3 py-3'
        }`}
      >
        {!mini && (
          <>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-indigo-100">
              <User size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white capitalize">{clientName}</div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sair"
              title="Sair"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-indigo-100 transition hover:bg-white/10 hover:text-white"
            >
              <LogOut size={18} />
            </button>
          </>
        )}
        {mini && (
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Sair"
            onMouseEnter={(event) => handleTooltipShow('Sair', event.currentTarget)}
            onMouseLeave={handleTooltipHide}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-indigo-100 transition hover:bg-white/10 hover:text-white"
          >
            <LogOut size={18} />
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
